import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import get from "lodash/get";
import { Button, Input, Select, Dropdown } from "antd";
import useAlert from "hooks/useAlert";
import { api } from "services/index";
import useTranslation from "hooks/useTranslation";
const SCALE = 5;
const DISPLAY_SCALE = 1.25;
const BARCODE_TYPE_ID = "db83218c-a8d0-41fe-b981-c38d280321be";
const SAVE_TEMPLATE_URL = "/tags";
const BARCODE_TYPES = [
	{ value: "CODE128", label: "CODE128" },
	{ value: "EAN13", label: "EAN-13" },
	{ value: "CODE39", label: "CODE39" },
];
const TEMPLATE_SIZE_OPTIONS = [
	{ width: 60, height: 30 },
	{ width: 40, height: 30 },
	{ width: 60, height: 40 },
	{ width: 30, height: 20 },
];

const PROPERTY_OPTIONS = [
	{ key: "name", label: "Наименование", type: "text", defaultText: "Kurtka XL" },
	{ key: "category", label: "Категория", type: "text", defaultText: "Категория" },
	{ key: "plu", label: "PLU", type: "text", defaultText: "PLU: 0001" },
	{ key: "article", label: "Артикул", type: "text", defaultText: "ART-0001" },
	{ key: "barcode", label: "Баркод", type: "barcode", defaultText: "2000000022833" },
	{ key: "price", label: "Цена", type: "text", defaultText: "799 000 UZS" },
	{ key: "unit", label: "Ед изм", type: "text", defaultText: "шт" },
];

const defaultProperty = (name, type = "text", index = 0) => {
	const isBarcode = type === "barcode";
	const defaultText = isBarcode ? "0000000000000" : "Новый текст";
	return {
		alignment_type: "LEFT",
		font_family: "Gilroy-Bold",
		font_size: isBarcode ? 0 : 18,
		image: defaultText,
		image_url: defaultText,
		is_bold: false,
		is_italic: false,
		is_underlined: false,
		is_line_through: false,
		length: isBarcode ? 18 : 6,
		name,
		type,
		rotation: 0,
		width: isBarcode ? 43 : 36,
		x_axis: 10,
		y_axis: isBarcode ? 8 + index * 10 : 0.6 + index * 7,
		is_qr_code: false,
	};
};

const initialTemplate = {
	name: "Yangi narx yorlig'i",
	width: TEMPLATE_SIZE_OPTIONS[0].width,
	length: TEMPLATE_SIZE_OPTIONS[0].height,
	barcode_type: "CODE128",
	barcode_type_id: BARCODE_TYPE_ID,
	properties: [
		{ ...defaultProperty("Наименование", "text"), name: "Наименование", image: "Продукт", image_url: "Basic ko'ylak", font_size: 16, y_axis: 1, x_axis: 2 },
		{ ...defaultProperty("Баркод", "barcode"), name: "Баркод", image: "2000000022833", image_url: "2000000022833", y_axis: 6, x_axis: 2, width: 36, length: 12 },
	],
};

const mapApiToTemplate = api => {
	const height = get(api, "height", get(api, "length"));
	return {
		name: get(api, "name", initialTemplate.name),
		width: Number(get(api, "width", initialTemplate.width)) || 40,
		length: Number(height != null ? height : initialTemplate.length) || 20,
		barcode_type: get(api, "barcode_type", initialTemplate.barcode_type),
		barcode_type_id: get(api, "barcode_type_id", BARCODE_TYPE_ID),
		properties: Array.isArray(get(api, "properties")) ? api.properties : initialTemplate.properties,
	};
};

export default function BarcodeConstructor() {
	const navigate = useNavigate();
	const { id: updateId } = useParams();
	const { showAlert } = useAlert();
	const { tr } = useTranslation();
	const [template, setTemplate] = useState(initialTemplate);
	const [selectedIndex, setSelectedIndex] = useState(null);
	const [saving, setSaving] = useState(false);
	const [loadingTemplate, setLoadingTemplate] = useState(!!updateId);
	const [draggingIndex, setDraggingIndex] = useState(null);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const selectedTemplateSize = `${template.width}x${template.length}`;
	const templateSizeOptions = useMemo(() => {
		const defaults = TEMPLATE_SIZE_OPTIONS.map(opt => ({
			value: `${opt.width}x${opt.height}`,
			label: `${opt.width}x${opt.height} mm`
		}));
		if (defaults.some(opt => opt.value === selectedTemplateSize)) {
			return defaults;
		}
		return [
			...defaults,
			{
				value: selectedTemplateSize,
				label: `${template.width}x${template.length} mm`
			}
		];
	}, [selectedTemplateSize, template.width, template.length]);

	useEffect(() => {
		if (!updateId) {
			setLoadingTemplate(false);
			return;
		}
		let cancelled = false;
		setLoadingTemplate(true);
		(async () => {
			try {
				const res = await api.request({
					method: "GET",
					url: `/tags/${updateId}`,
				});
				const raw = get(res, "data.data", get(res, "data"));
				if (cancelled || !raw) return;
				setTemplate(mapApiToTemplate(raw));
			} catch (err) {
				if (!cancelled) {
					const msg =
						get(err, "response.data.message") || get(err, "message") || tr("Ошибка загрузки");
					showAlert(msg, { type: "danger" });
					navigate("/settings/barcode-constructor");
				}
			} finally {
				if (!cancelled) setLoadingTemplate(false);
			}
		})();
		return () => {
			cancelled = true;
		};
		// Faqat updateId — showAlert/history har renderda yangi ref bo‘lishi mumkin, bo‘lmasa GET cheksiz takrorlanadi
	}, [updateId]);

	const stageW = Math.max(template.width * SCALE, 1);
	const stageH = Math.max(template.length * SCALE, 1);

	const updateProperty = useCallback((index, updates) => {
		setTemplate(prev => {
			const next = { ...prev };
			next.properties = [...next.properties];
			next.properties[index] = { ...next.properties[index], ...updates };
			return next;
		});
	}, []);

	const previewRef = React.useRef(null);
	const handlePointerDown = useCallback(
		(e, idx) => {
			e.preventDefault();
			if (e.button !== 0) return;
			const wrap = e.currentTarget.closest("[data-preview-wrap]");
			if (!wrap) return;
			const r = wrap.getBoundingClientRect();
			const labelX = (e.clientX - r.left) / DISPLAY_SCALE;
			const labelY = (e.clientY - r.top) / DISPLAY_SCALE;
			const prop = template.properties[idx];
			const elX = (prop.x_axis || 0) * SCALE;
			const elY = (prop.y_axis || 0) * SCALE;
			setDraggingIndex(idx);
			setDragStart({ x: labelX - elX, y: labelY - elY });
		},
		[template.properties]
	);

	useEffect(() => {
		if (draggingIndex == null) return;
		const onMove = e => {
			const wrap = previewRef.current && previewRef.current.closest("[data-preview-wrap]");
			if (!wrap) return;
			const r = wrap.getBoundingClientRect();
			const labelX = (e.clientX - r.left) / DISPLAY_SCALE;
			const labelY = (e.clientY - r.top) / DISPLAY_SCALE;
			const current = template.properties[draggingIndex];
			if (!current) return;
			const elementWidth =
				((current.type === "barcode" ? (current.width || 43) : (current.width || 40)) * SCALE);
			const elementHeight =
				((current.type === "barcode" ? (current.length || 18) : (current.length || 6)) * SCALE);
			const maxX = Math.max(0, (stageW - elementWidth) / SCALE);
			const maxY = Math.max(0, (stageH - elementHeight) / SCALE);
			const newX = Math.min(maxX, Math.max(0, (labelX - dragStart.x) / SCALE));
			const newY = Math.min(maxY, Math.max(0, (labelY - dragStart.y) / SCALE));
			updateProperty(draggingIndex, { x_axis: newX, y_axis: newY });
		};
		const onUp = () => setDraggingIndex(null);
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
		return () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
		};
	}, [draggingIndex, dragStart, stageW, stageH, template.properties, updateProperty]);

	const addProperty = key => {
		const option = PROPERTY_OPTIONS.find(o => o.key === key);
		if (!option) return;

		setTemplate(prev => {
			// Agar allaqachon shu nomdagi xususiyat bo'lsa, uni tanlaymiz, yangisini qo'shmaymiz
			const existingIdx = prev.properties.findIndex(p => p.name === option.label);
			if (existingIdx !== -1) {
				setSelectedIndex(existingIdx);
				return prev;
			}

			const count = prev.properties.length;
			const base = defaultProperty(option.label, option.type, count);
			const newProp = {
				...base,
				name: option.label,
				image: option.defaultText,
				image_url: option.defaultText,
				font_size: option.type === "text" ? 16 : base.font_size,
			};

			const nextProps = [...prev.properties, newProp];
			setSelectedIndex(nextProps.length - 1);

			return {
				...prev,
				properties: nextProps,
			};
		});
	};

	const removeProperty = index => {
		setTemplate(prev => ({
			...prev,
			properties: prev.properties.filter((_, i) => i !== index),
		}));
		if (selectedIndex === index) setSelectedIndex(null);
		else if (selectedIndex > index) setSelectedIndex(selectedIndex - 1);
	};

	const selectedProp = selectedIndex != null ? template.properties[selectedIndex] : null;

	const buildPayload = () => ({
		barcode_type: template.barcode_type,
		barcode_type_id: template.barcode_type_id,
		name: template.name,
		width: Number(template.width),
		height: Number(template.length),
		properties: template.properties.map(p => ({
			...p,
			font_size: Number(p.font_size) || 0,
			length: Number(p.length) || 0,
			width: Number(p.width) || 0,
			x_axis: Number(p.x_axis) || 0,
			y_axis: Number(p.y_axis) || 0,
			rotation: Number(p.rotation) || 0,
		})),
	});

	const handleSave = async () => {
		setSaving(true);
		try {
			const payload = buildPayload();
			await api.request({
				method: updateId ? "PUT" : "POST",
				url: updateId ? `/tags/${updateId}` : SAVE_TEMPLATE_URL,
				data: payload,
			});
			showAlert(tr("Шаблон сохранён"), { type: "success" });
			navigate("/settings/barcode-constructor");
		} catch (err) {
			const msg = get(err, "response.data.message") || get(err, "message") || tr("Ошибка сохранения");
			showAlert(msg, { type: "danger" });
		} finally {
			setSaving(false);
		}
	};

	const addPropertyMenu = {
		items: PROPERTY_OPTIONS.map(o => ({
			key: o.key,
			label: o.label,
		})),
		onClick: ({ key }) => addProperty(key),
	};

	if (loadingTemplate) {
		return (
			<div className="d-panel p-5 max-w-[1400px] mx-auto bg-[#f5f5f5] min-h-[calc(100vh-100px)] flex items-center justify-center">
				<div className="text-gray-600">{tr("Загрузка...")}</div>
			</div>
		);
	}

	return (
		<div className="d-panel p-5 max-w-[1400px] mx-auto bg-[#f5f5f5] min-h-[calc(100vh-100px)]">
			{/* Header */}
			<div className="flex items-center justify-between mb-4">
				<Button type="text" onClick={() => navigate(-1)} className="mb-0 text-gray-700">
					← {tr("Назад")}
				</Button>
				<h1 className="text-xl font-semibold m-0 text-gray-800">{tr(template.name)}</h1>
				<Button type="primary" onClick={handleSave} loading={saving}>
					{tr("Сохранить")}
				</Button>
			</div>

			{/* Yuqori qator: Nomi, Eni, Uzunlik, Shtrix-kod formati */}
			<div className="flex flex-wrap gap-4 mb-4">
				<div className="flex-1 min-w-[140px]">
					<label className="block text-sm font-medium mb-1 text-gray-700">{tr("Название")}</label>
					<Input
						value={template.name}
						onChange={e => {
							const value = get(e, "target.value", "");
							setTemplate(t => ({ ...t, name: value }));
						}}
						placeholder={tr("Название")}
					/>
				</div>
				<div className="w-[220px]">
					<label className="block text-sm font-medium mb-1 text-gray-700">{tr("Размер шаблона (мм)")}</label>
					<Select
						style={{ width: "100%" }}
						value={selectedTemplateSize}
						options={templateSizeOptions}
						onChange={value => {
							const [width, height] = String(value)
								.split("x")
								.map(v => Number(v));
							setTemplate(t => ({
								...t,
								width: Number.isFinite(width) ? width : t.width,
								length: Number.isFinite(height) ? height : t.length
							}));
						}}
					/>
				</div>
				<div className="w-[160px]">
					<label className="block text-sm font-medium mb-1 text-gray-700">{tr("Формат штрих-кода")}</label>
					<Select
						style={{ width: "100%" }}
						value={template.barcode_type}
						onChange={v => setTemplate(t => ({ ...t, barcode_type: v }))}
						options={BARCODE_TYPES}
					/>
				</div>
			</div>

			{/* Asosiy kartochka: chap panel + o'ng (toolbar + preview) */}
			<div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex">
				{/* Chap panel — xususiyatlar ro'yxati */}
				<div className="w-[220px] border-r border-gray-200 p-4 flex flex-col bg-gray-50/50">
					<div className="text-sm font-medium text-gray-700 mb-3">{tr("Свойства")}</div>
					<div className="space-y-2 flex-1">
						{template.properties.map((item, idx) => (
							<div
								key={`${item.name}-${idx}`}
								className={`flex items-center justify-between rounded-lg px-3 py-2 cursor-pointer border transition-colors ${selectedIndex === idx ? "bg-blue-50 border-blue-200" : "bg-white border-gray-200 hover:bg-gray-50"}`}
								onClick={() => setSelectedIndex(idx)}
							>
								<span className="text-sm truncate">{item.name}</span>
								<button
									type="button"
									className="text-red-500 hover:text-red-700 p-0.5 rounded"
									onClick={e => { e.stopPropagation(); removeProperty(idx); }}
									aria-label={tr("Удалить")}
								>
									×
								</button>
							</div>
						))}
					</div>
					<Dropdown menu={addPropertyMenu} trigger={["click"]}>
						<Button type="primary" block className="mt-3">
							+ {tr("Добавить свойство")}
						</Button>
					</Dropdown>
				</div>

				{/* O'ng qism: formatlash paneli + yorliq preview */}
				<div className="flex-1 flex flex-col min-w-0">
					{/* Formatlash toolbar — har doim ko'rinadi; tanlangan bo'lsa yoqiladi */}
					<div
						className={`border-b border-gray-200 px-4 py-3 flex flex-wrap items-center gap-3 transition-colors ${selectedIndex == null ? "bg-gray-50" : "bg-white"}`}
					>
						<Select
							size="small"
							style={{ width: 140 }}
							value={selectedProp ? (selectedProp.font_family || "Arial") : "Arial"}
							onChange={v => selectedIndex != null && updateProperty(selectedIndex, { font_family: v })}
							disabled={selectedIndex == null}
							options={[
								{ value: "Arial", label: "Arial" },
								{ value: "Gilroy-Bold", label: "Gilroy-Bold" },
							]}
						/>
						<Select
							size="small"
							style={{ width: 82 }}
							value={selectedProp ? (selectedProp.font_size || 16) : 16}
							onChange={v => selectedIndex != null && updateProperty(selectedIndex, { font_size: Number(v) || 16 })}
							disabled={selectedIndex == null}
							options={Array.from({ length: 41 }, (_, i) => {
								const size = i + 8;
								return { value: size, label: String(size) };
							})}
						/>
						<Button
							size="small"
							type={selectedProp && selectedProp.is_bold ? "primary" : "default"}
							onClick={() => selectedIndex != null && updateProperty(selectedIndex, { is_bold: !selectedProp.is_bold })}
							disabled={selectedIndex == null}
						>
							B
						</Button>
						<Button
							size="small"
							type={selectedProp && selectedProp.is_italic ? "primary" : "default"}
							onClick={() => selectedIndex != null && updateProperty(selectedIndex, { is_italic: !selectedProp.is_italic })}
							disabled={selectedIndex == null}
						>
							I
						</Button>
						<Button
							size="small"
							type={selectedProp && selectedProp.is_underlined ? "primary" : "default"}
							onClick={() => selectedIndex != null && updateProperty(selectedIndex, { is_underlined: !selectedProp.is_underlined })}
							disabled={selectedIndex == null}
						>
							U
						</Button>
						<Button
							size="small"
							type={selectedProp && selectedProp.is_line_through ? "primary" : "default"}
							onClick={() => selectedIndex != null && updateProperty(selectedIndex, { is_line_through: !selectedProp.is_line_through })}
							disabled={selectedIndex == null}
						>
							S
						</Button>
						<Select
							size="small"
							style={{ width: 90 }}
							value={selectedProp ? (selectedProp.alignment_type || "LEFT") : "LEFT"}
							onChange={v => selectedIndex != null && updateProperty(selectedIndex, { alignment_type: v })}
							disabled={selectedIndex == null}
							options={[
								{ value: "LEFT", label: "Chap" },
								{ value: "CENTER", label: "Markaz" },
								{ value: "RIGHT", label: "O'ng" },
							]}
						/>
						<span className="text-gray-400">|</span>
						{/* <Input
							size="small"
							className="flex-1 min-w-[120px] max-w-[200px]"
							placeholder={selectedIndex == null ? "Xususiyatni tanlang" : "Matn / qiymat"}
							value={selectedProp ? (selectedProp.image || selectedProp.image_url || "") : ""}
							onChange={e => selectedIndex != null && updateProperty(selectedIndex, { image: e.target.value, image_url: e.target.value })}
							disabled={selectedIndex == null}
						/> */}
					</div>

					{/* Yorliq preview — aniq oq ramka */}
					<div className="flex-1 p-6 flex items-start justify-center overflow-auto bg-gray-100/80">
						<div
							ref={previewRef}
							data-preview-wrap
							className="rounded-lg border-2 border-gray-300 overflow-hidden flex-shrink-0 bg-white"
							style={{
								width: stageW * DISPLAY_SCALE,
								height: stageH * DISPLAY_SCALE,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
							}}
						>
							<div
								className="relative cursor-default select-none origin-top-left"
								style={{
									width: stageW,
									height: stageH,
									transform: `scale(${DISPLAY_SCALE})`,
								}}
							>
								<div
									className="relative w-full h-full overflow-hidden"
									style={{ width: stageW, height: stageH }}
									onMouseDown={e => e.target === e.currentTarget && setSelectedIndex(null)}
								>
									{template.properties.map((prop, idx) => {
										const x = (prop.x_axis || 0) * SCALE;
										const y = (prop.y_axis || 0) * SCALE;
										const w = (prop.width || 40) * SCALE;
										const h = (prop.length || 6) * SCALE;
										const text = (prop.image || prop.image_url || prop.name || "").trim() || prop.name || "—";
										const isSelected = selectedIndex === idx;
										const isBarcode = prop.type === "barcode";
										if (isBarcode) {
											const bw = (prop.width || 43) * SCALE;
											const bh = (prop.length || 18) * SCALE;
											return (
												<div
													key={`html-${prop.name}-${idx}`}
													role="button"
													tabIndex={0}
													className={`absolute bg-white flex flex-col justify-end cursor-move rounded-sm ${isSelected ? "ring-2 ring-blue-400 ring-offset-1" : ""}`}
													style={{
														left: x,
														top: y,
														width: bw,
														height: bh,
													}}
													onClick={e => { e.stopPropagation(); setSelectedIndex(idx); }}
													onPointerDown={e => { e.stopPropagation(); handlePointerDown(e, idx); }}
												>
													<div className="flex flex-1 w-full items-stretch overflow-hidden px-0.5" style={{ minHeight: 6 }}>
														{[...Array(30)].map((_, i) => (
															<div
																key={i}
																className="bg-black flex-shrink-0"
																style={{
																	width: (i % 2 === 0 ? 1.2 : 0.8) * Math.max(1, bw / 35),
																	marginRight: i < 29 ? 1 : 0,
																}}
															/>
														))}
													</div>
													<span className="text-[10px] leading-tight pb-0.5 block text-center">{text}</span>
												</div>
											);
										}
										return (
											<div
												key={`html-${prop.name}-${idx}`}
												role="button"
												tabIndex={0}
												className={`absolute whitespace-nowrap cursor-move rounded-sm ${isSelected ? "ring-2 ring-blue-400 ring-offset-1" : ""}`}
												style={{
													left: x,
													top: y,
													width: w,
													height: h,
													fontSize: ((prop.font_size || 14) * SCALE) / 5,
													fontFamily: prop.font_family || "Arial",
													fontWeight: prop.is_bold ? "bold" : "normal",
													fontStyle: prop.is_italic ? "italic" : "normal",
													textDecoration: [prop.is_underlined && "underline", prop.is_line_through && "line-through"].filter(Boolean).join(" ") || "none",
													textAlign: (prop.alignment_type || "LEFT").toLowerCase(),
												}}
												onClick={e => { e.stopPropagation(); setSelectedIndex(idx); }}
												onPointerDown={e => { e.stopPropagation(); handlePointerDown(e, idx); }}
											>
												{text}
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
